import { Button, Loading } from 'components'
import { PreUnlockModal } from 'components/ConfirmationModals/PreUnlockModal'
import moment from 'moment'
import { useUsersActions } from 'providers'
import { useAlert } from 'providers/confirmationModal'
import { useCallback, useState } from 'react'
import { UserType } from 'types'
import { noop } from 'utils'

export type UnlockCourseBtnProps = {
  values: {
    instructionDate: string | undefined
    countryCode: string | undefined
    teachingOrg?: string
    userType?: UserType
    id: string
  }
  isCustomerSupportUser?: boolean
  disabled?: boolean
  onDone?: (message: string) => void
  onError?: (message: string) => void
}
export const UnlockCourseBtn = ({
  disabled = false,
  values,
  isCustomerSupportUser = false,
  onDone = noop,
  onError = noop,
}: UnlockCourseBtnProps) => {
  // using useUserActions so we can access the raw async function but still have ui side effects
  const {
    account: { unlock },
  } = useUsersActions()
  const [loading, setLoading] = useState<boolean>(false)
  const {
    createAlert,
    interceptConfirm,
    finalizeConfirm,
    abortConfirm,
    handleCancel,
  } = useAlert()
  const shouldShowConfirmation =
    !values.instructionDate ||
    moment(values.instructionDate).isBefore(moment().format('YYYY-MM-DD'))

  const unlockUser = useCallback(() => {
    //needs to be a .then because when the user updates, this component won't exist, but the modal still will need to be closed
    setLoading(true)
    unlock({ userId: values.id })
      .then(() => {
        setLoading(false)
        finalizeConfirm()
        onDone('User has been Unlocked')
      })
      .catch((e) => {
        setLoading(false)
        abortConfirm('Unlock Failed, Try Again!')
        onError('Unlock Failed, Try Again!')
      })
  }, [
    unlock,
    values.id,
    finalizeConfirm,
    abortConfirm,
    setLoading,
    onDone,
    onError,
  ])

  function handleUnlock() {
    if (shouldShowConfirmation) {
      createAlert({
        content: (
          <PreUnlockModal
            values={values}
            interceptConfirm={interceptConfirm}
            continueConfirm={unlockUser}
            abortConfirm={abortConfirm}
            cancel={handleCancel}
            isCustomerSupportUser={isCustomerSupportUser}
          />
        ),
        confirmText: 'Unlock',
      })
    } else {
      unlockUser()
    }
  }

  return (
    <>
      <Button
        variant="outlinePrimary"
        type="button"
        disabled={disabled || loading}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 90,
          height: 40,
          mx: 5,
        }}
        onClick={handleUnlock}>
        {loading && !shouldShowConfirmation ? <Loading /> : <span>Unlock</span>}
      </Button>
    </>
  )
}
